import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X } from 'lucide-react';

const Products = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const productImages = [
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/a3053e65-04a1-45a3-9021-7934656d6322.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/6b9c4e01-5016-49d1-8835-abdea87fc55c.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/b54a88e2-400d-40dc-a602-888ab0fc5294.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/d5e5fc29-358d-4019-9ecf-e9297527f851.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/c28dc8d6-bd68-4ba0-83ff-8938819b039d.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/3e3ce24a-becb-4c5f-b6aa-76891516e17e.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/bf348a6f-439c-409e-800a-bff81eb2d97a.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/03505ef0-3d71-4b7e-80b2-a8d5a6421996.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/74a50d1d-96c2-4f76-b9df-8fdf87c85e73.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/887ee401-7725-4de9-81e1-7656c98ee46f.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/4797ebcf-dd27-4483-8356-873c50be1ce8.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/b52dc741-8e34-4f3b-ac45-b6d02f3f5a92.jpg"
  ];

  return (
    <div className="bg-white">
      <Helmet>
        <title>Produkter — Toftes Hundesalon</title>
        <meta name="description" content="Eksklusivt hundeudstyr fra Cloud7 i salonen. Vi tilbyder bæredygtige og minimalistiske produkter til din hund." />
        <meta property="og:title" content="Produkter — Toftes Hundesalon" />
      </Helmet>

      {/* Product Grid */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-4">Kollektion</h2>
            <h3 className="!text-[1.6rem] md:text-6xl font-serif italic text-stone-900">Se udvalget i salonen</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {productImages.map((img, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 0.98 }}
                className="aspect-square overflow-hidden shadow-sm cursor-pointer"
                onClick={() => setSelectedImage(img)}
              >
                <img 
                  src={img} 
                  alt={`Cloud7 Produkt ${idx + 1}`} 
                  className="w-full h-full object-cover transition-all duration-500"
                />
              </motion.div>
            ))}
          </div>

          {/* Lightbox */}
          <AnimatePresence>
            {selectedImage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedImage(null)}
                className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
              >
                <button 
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-6 right-6 text-white hover:text-stone-400 transition-colors z-[110]"
                >
                  <X className="w-10 h-10" />
                </button>
                <motion.img
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  src={selectedImage}
                  alt="Produkt i stor størrelse"
                  className="max-w-full max-h-full object-contain shadow-2xl"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-4 bg-stone-100 text-stone-900 relative overflow-hidden border-t border-stone-200">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-stone-200/50 -skew-x-12 translate-x-1/2" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-sm uppercase tracking-[0.6em] font-bold mb-8 text-stone-600">Kvalitet</h2>
            <h3 className="!text-[1.6rem] md:text-6xl font-serif italic mb-12 leading-tight text-stone-900">
              Design der holder <br /> i årevis
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { title: "Bæredygtigt", desc: "Materialer valgt med omhu for miljøet." },
              { title: "Minimalistisk", desc: "Design der passer ind i det moderne hjem." },
              { title: "Holdbart", desc: "Kvalitet der tåler hverdagens brug." }
            ].map((feature, idx) => (
              <div key={idx} className="p-12 border border-stone-300 bg-white">
                <h4 className="text-2xl font-serif italic mb-4 text-stone-900">{feature.title}</h4>
                <p className="text-stone-600 leading-relaxed italic">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
